/** @jsx jsx */
import { jsx, useThemeUI } from "theme-ui"
import { useState, useEffect, useContext } from "react"

import NavigationContext from "../../context/navigation-context"

import Link, { TransformLinkUri } from "../link"
import Icon from "../../assets/icon"

import Layer from "./layer"
import LayerMobile from "./layer-mobile"
import { ab } from "../../theme"
import { isHomepagePath } from "../../utils/mark-current-nav"
import PropTypes from "prop-types"
import { px } from "framer-motion"

const NavigationItem = ({
  item,
  level,
  language,
  mode,
  selected,
  hasChild,
  location,
  setSelected,
  setSubLayer,
  mainSiteLinkText,
}) => {
  const [isCurrent, setIsCurrent] = useState(false)
  const [initialOpen, setInitialOpen] = useState(true)
  const [toggleNavMobile, setToggleNavMobile] = useState(selected)

  const { resetLevel } = useContext(NavigationContext)

  const closeSubNavi = () => {
    setTimeout(() => {
      setSubLayer(null)
    }, 300)
  }
  let itemExtras

  if (item.extras && item.extras.length) {
    itemExtras = item.extras
  }

  const currentUrl = item.url.id
    ? TransformLinkUri(item.url.id, null, language)
    : null

  // Don't highlight anything when the user is on the homepage.
  const onHomepage = isHomepagePath(location.pathname, language)

  // A bare "/" or "/<lang>/" is a prefix of every page on the site, so
  // startsWith() would falsely mark home-pointing entries (e.g. the top
  // entry of a submenu that links back to the section root) as active on
  // every deeper page. Require either an exact match or a segment break
  // beyond the parent URL.
  const isRootLink =
    !currentUrl || currentUrl === "/" || currentUrl === `/${language}/`
  const isCurrentPage = !onHomepage && currentUrl === location.pathname
  const isPartiallyCurrent =
    !onHomepage &&
    !isRootLink &&
    !isCurrentPage &&
    location.pathname.startsWith(currentUrl) &&
    (currentUrl.endsWith("/") ||
      location.pathname.charAt(currentUrl.length) === "/")
  const isActive =
    !onHomepage &&
    (isPartiallyCurrent || isCurrentPage || item.currentPage || item.open)

  if (
    (isPartiallyCurrent || isCurrentPage || item.open) &&
    initialOpen &&
    hasChild
  ) {
    setInitialOpen(false)
    setIsCurrent(true)
    setSelected()
    resetLevel(level)
    setSubLayer(
      <Layer
        parent={item.displayName}
        parentUrl={item.url.id}
        parentSearch={item.search}
        items={item.children}
        language={language}
        level={level}
        mode={item.mode || mode}
        location={location}
        closeSubNavi={closeSubNavi}
        parentIcon={item.icon}
        itemExtras={itemExtras}
        mainSiteLinkText={mainSiteLinkText}
      />
    )
  }

  const openLayer = () => {
    setIsCurrent(true)
    setSubLayer(
      <Layer
        parent={item.displayName}
        parentUrl={item.url.id}
        parentSearch={item.search}
        items={item.children}
        language={language}
        level={level}
        mode={item.mode || mode}
        location={location}
        closeSubNavi={closeSubNavi}
        parentIcon={item.icon}
        itemExtras={itemExtras}
        mainSiteLinkText={mainSiteLinkText}
      />
    )
  }

  useEffect(() => {
    setToggleNavMobile(selected ? true : !toggleNavMobile)
  }, [selected])

  const { rawColors: colors } = useThemeUI().theme

  // Visual rules:
  //   level 2 (first drawer): bold + underline on active item, no indicator
  //   level >= 3 (nested drawer): bold + underline + 2px vertical indicator
  const isLineActive = isActive && level > 2

  return (
    <div
      className={`item${isLineActive ? " active" : ""}`}
      sx={{
        variant: isLineActive ? "navigation.activeIndicator" : undefined,
        li: {
          px: "20px",
        },
        ".toggleIcon": {
          mr: "20px",
        },
        ".pLevel": {
          "&:after": {
            width: "100%",
          },
        },
        ".inactive": {
          maxHeight: 0,
          overflow: "hidden",
          transition: "all .3s ease-out",
          opacity: 0,
          ".mobile-navigation-layer": {
            maxHeight: 0,
            overflow: "hidden",
            transition: "all .3s ease-out ",
          },
        },
        ".active": {
          maxHeight: ab({ _: "2000px", md: 0 }),
          opacity: 1,
          overflow: ab({ _: "visible", md: "hidden" }),
          transition: "all .3s ease-in",
          ".mobile-navigation-layer": {
            maxHeight: "2000px",
            transition: "all .3s ease-out",
          },
        },
      }}
    >
      <div
        sx={{
          display: "flex",
          lineHeight: "1.8",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {item.icon && (
          <div
            sx={{
              height: ab({ _: "34px", md: "28px", lg: "34px" }),
              width: ab({ _: "34px", md: "28px", lg: "34px" }),
              mr: ab({ _: "15px", md: "10px", lg: "15px" }),

              display: "inherit",
              flexShrink: 0,
            }}
          >
            <Icon style={{ fill: colors.navigation.icon }} icon={item.icon} />
          </div>
        )}
        <div
          className="item-link"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "left",
            flexGrow: 2,
          }}
        >
          <Link
            onClick={(e) => {
              if (hasChild) {
                e.preventDefault()
                resetLevel(level)
                openLayer()
                setToggleNavMobile(!toggleNavMobile)
              }
            }}
            to={
              item.url.id || item.url.url
                ? item.url.id || item.url.url
                : item.mainSiteLink
                  ? item.mainSiteLink.url
                  : item.url.id || item.url.url
            }
            search={item.search}
            activeClassName="current"
            className={isActive ? "active-link" : ""}
            aria-current={isCurrentPage ? "page" : undefined}
            sx={{
              variant: level > 2 ? "navigation.hover" : "",
              fontSize: ab({ _: "14px", md: "13px", lg: "14px" }),
              fontWeight:
                isActive || (item.mainSiteLink && item.mainSiteLink.url)
                  ? "bold"
                  : "",
              textDecoration: isActive ? "underline" : undefined,
            }}
            language={language}
          >
            {item.displayName}
            {item.linkSubText && (
              <span
                sx={{
                  display: "block",
                  fontWeight: "normal",
                  pb: "5px",
                }}
              >{` ${item.linkSubText}`}</span>
            )}
          </Link>
          {item.mainSiteLink && item.mainSiteLink.url && (
            <Link
              to={
                item.mainSiteLink && item.mainSiteLink.url
                  ? item.mainSiteLink.url
                  : "#"
              }
              target="_blank"
              sx={{
                fontSize: ab({ _: "14px", md: "13px", lg: "14px" }),
                display: "inline-block",
                mt: 0,
                mb: 0,
              }}
              language={language}
            >
              <span
                sx={{
                  pt: "1px",
                  display: "flex",
                  alignItems: "flex-end",
                  textDecoration: "underline",
                  svg: {
                    position: "relative",
                    left: "5px",
                    width: "20px",
                    height: "20px",
                  },
                }}
              >
                {mainSiteLinkText}
                <Icon style="#fff" icon={"externalLink"} />
              </span>
            </Link>
          )}
        </div>

        {hasChild && (
          <div
            className="toggleIcon"
            sx={{
              transform: ab({
                _:
                  level > 2 && selected && toggleNavMobile
                    ? "rotate(-90deg)"
                    : "rotate(90deg)",
                md: "rotate(0)",
              }),
              height: 20,
              width: 20,
              zIndex: 0,
              transition: "all .3s",
              flexShrink: "0",
            }}
          >
            <Icon style={{ fill: colors.navigation.icon }} icon="right" />
          </div>
        )}
      </div>
      {level > 2 && hasChild && (
        <div
          className={`mobile-nav
                ${selected && toggleNavMobile ? "active" : "inactive"}
                `}
        >
          <LayerMobile
            parent={item.displayName}
            parentUrl={item.url.id}
            parentSearch={item.search}
            items={item.children}
            language={language}
            mode={mode}
            location={location}
            level={level}
            itemExtras={itemExtras}
            mainSiteLinkText={mainSiteLinkText}
          />
        </div>
      )}
    </div>
  )
}
NavigationItem.protoTypes = {
  item: PropTypes.array.isRequired,
  level: PropTypes.number.isRequired,
  language: PropTypes.string.isRequired,
  mode: PropTypes.oneOf([
    "group",
    "pharma",
    "food",
    "environment",
    "career",
    "sustainabilityServices",
  ]).isRequired,
  selected: PropTypes.bool,
  hasChild: PropTypes.bool,
  location: PropTypes.object.isRequired,
  setSelected: PropTypes.bool,
  setSubLayer: PropTypes.object.isRequired,
}

export default NavigationItem
